import React, { useEffect, useState } from "react";

import { useRouter } from "next/router";
import Link from "next/link";

import { useAppState } from "lib/appState";
import { googleLoginApi, registerApi } from "api/auth.api";
import Head from "next/head";
import Loader from "components/atoms/Loader";
import {
  Alert,
  Checkbox,
  CssBaseline,
  FormControlLabel,
  Typography,
} from "@mui/material";

import Button from "components/Button/Button";
import { Box } from "@mui/system";
import ReworkedLayout from "layouts/MarvyLayout";
import MarvyTextField from "components/atoms/MarvyTextField";
import { emitGTMEvent } from "lib/events";
import LoginWithGoogleButton from "components/LoginWithGoogleButton";
import { MT } from "components/MarvyTypography";
import Row from "components/atoms/Row";
import { saveLoginSession } from "lib/auth";
import { errorName, getErrorMessage } from "lib/errors";
import features from "config/features";

function Field({ ...props }) {
  return <MarvyTextField sx={{ mb: "30px" }} {...props} />;
}

export default function Register() {
  const router = useRouter();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    name: "",
    surname: "",
    email: "",
    password: "",
  });
  const [appState, setAppState] = useAppState();

  const passwordValid =
    data.password.length >= 8 &&
    data.password.match(/\d/) &&
    data.password.match(/[a-zA-Z]/);

  useEffect(() => {
    if (appState.tokens) {
      router.push("/dashboard");
    }
  }, [appState, router]);

  async function register(data) {
    try {
      setLoading(true);
      const res = await registerApi(data);
      await emitGTMEvent({ event: "register" });
      await router.push(`/auth/registered?e=${data.email}`);
      setAppState({ ...appState, registeredUser: res.data });
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setError(errorName(e));
    }
  }

  function submit(e) {
    e.preventDefault();
    if (passwordValid) {
      register(data);
    }
  }

  async function onGoogleLogin(data) {
    setLoading(true);
    await setAppState({ ...appState, googleLoginCredential: data.credential });
    try {
      const res = await googleLoginApi({
        credential: data.credential,
      });

      await saveLoginSession({
        user: res.data.user,
        tokens: res.data.tokens,
        appState,
        setAppState,
        router,
      });
    } catch (e) {
      if (e.response?.data.name === "user_doesnt_exist") {
        await router.push("/auth/register/google");
      } else {
        setError(errorName(e));
      }
    }
    setLoading(false);
  }

  return (
    <ReworkedLayout noMenu unauthenticated>
      <Head>
        <title>Sign up to Uptimeline</title>
        <meta
          name="description"
          content="With UptimeLine you get up to 50 uptime monitors for free, forever!"
        />
      </Head>
      {features.googleSingIn ? (
        <>
          <Row sx={{ justifyContent: "center" }}>
            <LoginWithGoogleButton onLogin={onGoogleLogin} />
          </Row>
          <MT sx={{ alignSelf: "center", my: 4 }}>OR</MT>
        </>
      ) : null}
      <Box
        sx={{
          paddingBottom: "30px",
        }}
      >
        <Typography
          // variant="h1"
          sx={{
            fontWeight: "700",
            fontSize: "24px",
            color: "white.main",
          }}
          align="center"
        >
          Sign up with your email
        </Typography>
      </Box>
      {loading ? <Loader /> : null}
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignSelf: "stretch",
          alignItems: "stretch",
          px: "12px",
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ marginBottom: "40px" }}>
            {" "}
            {getErrorMessage(error)}
          </Alert>
        ) : null}

        <CssBaseline />
        <Field
          type="email"
          label="Work Email Address"
          name="email"
          placeholder="john.doe@example.com"
          value={data.email}
          required
          onChange={(e) => setData({ ...data, email: e.target.value })}
        />
        <Field
          type="password"
          label="Password"
          name="password"
          placeholder="Letters and numbers, at least 8 characters"
          error={data.password && !passwordValid}
          value={data.password}
          required
          onChange={(e) => setData({ ...data, password: e.target.value })}
        />

        <Box sx={{ height: "2.5rem" }} />

        <Field
          type="text"
          label="Name"
          name="name"
          placeholder="John"
          value={data.name}
          required
          onChange={(e) => setData({ ...data, name: e.target.value })}
        />
        <Field
          type="text"
          label="Surname"
          name="surname"
          placeholder="Doe"
          value={data.surname}
          required
          onChange={(e) => setData({ ...data, surname: e.target.value })}
        />
        {/* <Box sx={{ height: "2.5rem" }} /> */}

        <FormControlLabel
          control={
            <Checkbox
              sx={{
                color: "rgba(255, 255, 255, 0.5)",
              }}
              checked={data.agreeToNewsletter}
              onChange={(e) =>
                setData({
                  ...data,
                  agreeToNewsletter: e.target.checked,
                })
              }
            />
          }
          label="Inform me about new features and updates (max. twice a month)"
        />

        <Typography
          sx={{
            fontSize: "0.9rem",
            opacity: 0.9,
            textAlign: "center",
            marginTop: 5,
          }}
        >
          By creating an account you are accepting the{" "}
          <a href="/privacy" target="_blank" className="text-lightBlue-500">
            Privacy Policy
          </a>
          &nbsp;and&nbsp;
          <a href="/terms" target="_blank" className="text-lightBlue-500">
            Terms and Conditions
          </a>
          .
        </Typography>
        <Box sx={{ height: "2.5rem" }} />
        <Button
          sx={{ mb: "60px" }}
          type="submit"
          gradient
          disabled={!passwordValid}
        >
          Create Account
        </Button>
        <Link href="/auth/login">
          <Button variant="text">Log in instead</Button>
        </Link>
      </Box>
    </ReworkedLayout>
  );
}
